import { Injectable, DestroyRef, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, IRetryPolicy, RetryContext } from '@microsoft/signalr';
import { BehaviorSubject, map, Subject } from 'rxjs';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationTypeEnum } from '../enums/task.enums';
import { AuthEventsService } from './auth-events.service';
import { AuthService } from './auth.service';

export interface TaskNotification {
  id: string;
  type: NotificationTypeEnum;
  message: string;
  taskId?: string;
  taskType?: string;
  taskTitle?: string;
  timestamp: Date;
  isRead: boolean;
}

export type HealthStatus = 'Online' | 'Limited' | 'Outage';

export interface ProviderStatus {
  provider: string;
  isActive: boolean;
  priority: number;
  status: 'Online' | 'Outage' | 'Outlier' | 'Limited';
  quote?: number | null;
  deviationPercent?: number | null;
}

export interface HealthServiceStatus {
  service: string;
  status: HealthStatus;
  providers?: ProviderStatus[] | null;
  currencies?: HealthCurrencyStatus[] | null;
}

export interface HealthCurrencyProvider {
  name: string;
  hasOutage: boolean;
  status: 'Online' | 'Outage' | 'Outlier' | 'Limited';
  priority: number;
  isActive: boolean;
  quote?: number | null;
  deviationPercent?: number | null;
}

export interface HealthCurrencyStatus {
  currency: string;
  displayPrecision: number;
  overridePrecision: number;
  status: HealthStatus;
  rate: number;
  overriddenRate?: number | null;
  outageStartedAt?: string | null;
  overrideStartedAt?: string | null;
  providers: HealthCurrencyProvider[];
}

export interface BlockchainStatus {
  blockchain: string;
  blockchainHeight: number;
  lastProcessedBlock: number;
  status: 'On sync' | 'Out of sync' | 'Behind sync' | 'Outage';
}

export interface SystemHealthPayload {
  lastChecked: Date;
  services: HealthServiceStatus[];
  blockchains: BlockchainStatus[];
}

export const RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000];
export const STABLE_CONNECTION_MS = 10000;

export class UnstableConnectionBackoffPolicy implements IRetryPolicy {
  private attempt = 0;
  private connectedAt: number | null = null;

  constructor(private readonly now: () => number = Date.now) {}

  reset(): void {
    this.attempt = 0;
    this.connectedAt = null;
  }

  markConnected(): void {
    this.connectedAt = this.now();
  }

  nextRetryDelayInMilliseconds(context: RetryContext): number | null {
    const wasStable = this.connectedAt !== null && this.now() - this.connectedAt >= STABLE_CONNECTION_MS;
    if (context.previousRetryCount === 0 && wasStable) {
      this.attempt = 0;
    }

    const delay = RECONNECT_DELAYS_MS[Math.min(this.attempt, RECONNECT_DELAYS_MS.length - 1)];
    this.attempt++;

    return delay;
  }
}

function pricingCurrencies(payload: SystemHealthPayload | null): HealthCurrencyStatus[] | null {
  const pricing = payload?.services?.find((service) => service.service.toLowerCase().includes('pricing'));

  return pricing?.currencies ?? null;
}

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private authService = inject(AuthService);
  private authEvents = inject(AuthEventsService);
  private destroyRef = inject(DestroyRef);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);

  private hubConnection: HubConnection | null = null;
  private connectionStateSubject = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  private notificationsSubject = new BehaviorSubject<TaskNotification[]>([]);
  private healthStatusSubject = new BehaviorSubject<SystemHealthPayload | null>(null);
  private onlineUsersSubject = new BehaviorSubject<string[]>([]);
  private taskQueueChangedSubject = new Subject<void>();

  public connectionState$ = this.connectionStateSubject.asObservable();
  public notifications$ = this.notificationsSubject.asObservable();
  public healthStatus$ = this.healthStatusSubject.asObservable();
  public healthCurrencies$ = this.healthStatusSubject.pipe(map(pricingCurrencies));
  public onlineUsers$ = this.onlineUsersSubject.asObservable();
  public taskQueueChanged$ = this.taskQueueChangedSubject.asObservable();

  private notifications: TaskNotification[] = [];
  private maxNotifications = 50;
  private intentionallyDisconnected = false;
  private healthMonitoringRequested = false;
  private readonly retryPolicy = new UnstableConnectionBackoffPolicy();

  constructor() {
    // Disconnect from hubs on any logout (manual or session expired)
    this.authEvents.logout$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.intentionallyDisconnected = true;
      // attempt to leave any user-specific groups gracefully
      this.leaveUserGroup().finally(async () => {
        await this.disconnect();
        // clear in-memory state related to live updates
        this.clearNotifications();
        this.healthMonitoringRequested = false;
        this.healthStatusSubject.next(null);
        this.onlineUsersSubject.next([]);
      });
    });

    // Attempt initial connection if token is present
    this.startConnection();

    // React to auth state changes: connect on login, disconnect on logout
    this.authService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user) {
        // allow reconnection after explicit logout
        this.intentionallyDisconnected = false;
        if (!this.isConnected()) {
          this.startConnection();
        }
      } else {
        // logout or unauthenticated state: ensure disconnection
        this.intentionallyDisconnected = true;
        this.leaveUserGroup().finally(async () => {
          await this.disconnect();
          this.clearNotifications();
          this.healthMonitoringRequested = false;
          this.healthStatusSubject.next(null);
          this.onlineUsersSubject.next([]);
        });
      }
    });
  }

  private async startConnection(): Promise<void> {
    if (this.intentionallyDisconnected) {
      return;
    }

    // A connection still starting or reconnecting counts as live; building another would leave
    // the first one orphaned, so every hub push would arrive twice
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
      return;
    }

    const accessToken = this.authService.getAccessToken();

    if (!accessToken) {
      console.error('No access token available for SignalR connection');
      return;
    }
    const hubUrl = `${this.cfg.baseUrl}/taskHub`;

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => this.authService.getAccessToken() ?? '',
      })
      .withAutomaticReconnect(this.retryPolicy)
      .build();

    // Set up event handlers
    this.setupEventHandlers();

    try {
      await this.hubConnection.start();
      this.retryPolicy.markConnected();
      this.connectionStateSubject.next(this.hubConnection.state);

      // Join global group for general notifications
      await this.hubConnection.invoke('JoinGlobalGroup');

      // Restore health monitoring subscription after a fresh connection
      await this.restoreHealthMonitoring();

      // Request the current list of online users
      await this.requestOnlineUsers();
    } catch (err) {
      if (this.hubConnection.state === HubConnectionState.Connected) {
        await this.hubConnection.stop();
        return;
      }

      this.connectionStateSubject.next(this.hubConnection.state);

      // Retry connection after 5 seconds unless explicitly logged out
      if (this.hubConnection.state === HubConnectionState.Disconnected && !this.intentionallyDisconnected) {
        setTimeout(() => this.startConnection(), 5000);
      }
    }
  }

  private setupEventHandlers(): void {
    if (!this.hubConnection) return;

    this.cleanupSignalREventHandlers();

    // Connection state changes
    this.hubConnection.onclose(() => {
      this.connectionStateSubject.next(HubConnectionState.Disconnected);
      // Attempt to reconnect unless explicitly logged out
      if (!this.intentionallyDisconnected) {
        setTimeout(() => this.startConnection(), 5000);
      }
    });

    this.hubConnection.onreconnecting(() => {
      this.connectionStateSubject.next(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected(() => {
      this.retryPolicy.markConnected();
      this.connectionStateSubject.next(HubConnectionState.Connected);
      // Refresh health and presence data after reconnection
      this.restoreHealthMonitoring();
      this.requestOnlineUsers();
    });

    // Task notifications
    this.hubConnection.on('ReceiveTaskNotification', (message: string, data: TaskNotification) => {
      const notification: TaskNotification = {
        id: data.id,
        type: data.type,
        message,
        timestamp: new Date(),
        isRead: false,
        taskId: data.taskId,
        taskType: data.taskType,
        taskTitle: data.taskTitle,
      };

      this.addNotification(notification);
    });

    // Task queue changes
    this.hubConnection.on('ReceiveTaskQueueChanged', () => {
      this.taskQueueChangedSubject.next();
    });

    // Health monitoring notifications
    this.hubConnection.on('ReceiveHealthStatus', (payload: SystemHealthPayload) => {
      this.healthStatusSubject.next(payload);
    });

    // Presence: single user update
    this.hubConnection.on('ReceivePresenceUpdate', (payload: { userId: string; isOnline: boolean }) => {
      if (!payload || !payload.userId) return;
      const current = new Set(this.onlineUsersSubject.value);
      if (payload.isOnline) {
        current.add(payload.userId);
      } else {
        current.delete(payload.userId);
      }
      this.onlineUsersSubject.next([...current]);
    });

    // Presence: full list snapshot
    this.hubConnection.on('ReceiveOnlineUsers', (userIds: string[]) => {
      if (!Array.isArray(userIds)) return;
      // Ensure unique entries
      const unique = Array.from(new Set(userIds.filter((x) => !!x)));
      this.onlineUsersSubject.next(unique);
    });
  }

  private cleanupSignalREventHandlers(): void {
    if (!this.hubConnection) return;
    this.hubConnection.off('ReceiveTaskNotification');
    this.hubConnection.off('ReceiveTaskQueueChanged');
    this.hubConnection.off('ReceiveHealthStatus');
    this.hubConnection.off('ReceivePresenceUpdate');
    this.hubConnection.off('ReceiveOnlineUsers');
  }

  private mapNotificationType(taskType?: string): NotificationTypeEnum | undefined {
    // Map task types to notification types if needed
    // This is a placeholder implementation
    return undefined;
  }

  private addNotification(notification: TaskNotification): void {
    this.notifications.unshift(notification);

    // Keep only the latest notifications
    if (this.notifications.length > this.maxNotifications) {
      this.notifications = this.notifications.slice(0, this.maxNotifications);
    }

    this.notificationsSubject.next([...this.notifications]);
  }

  public async joinUserGroup(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('JoinUserGroup');
      } catch (err) {
        // Error handled silently
      }
    }
  }

  public async leaveUserGroup(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('LeaveUserGroup');
      } catch {
        // Leaving is best-effort
      }
    }
  }

  /** Bootstraps the health state from a REST snapshot; a live push always wins, so nothing is overwritten. */
  public seedHealthStatus(payload: SystemHealthPayload | null): void {
    if (!payload || this.healthStatusSubject.value) return;

    this.healthStatusSubject.next(payload);
  }

  public async joinHealthMonitoringGroup(): Promise<void> {
    this.healthMonitoringRequested = true;

    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('JoinHealthMonitoringGroup');
      } catch (err) {
        // Error handled silently
      }
    }
  }

  private async restoreHealthMonitoring(): Promise<void> {
    if (!this.healthMonitoringRequested) return;

    await this.joinHealthMonitoringGroup();
    await this.requestHealthStatus();
  }

  public async requestHealthStatus(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('RequestHealthStatus');
      } catch (err) {
        // Error handled silently
      }
    }
  }

  public async requestOnlineUsers(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      try {
        await this.hubConnection.invoke('RequestOnlineUsers');
      } catch (err) {
        // Error handled silently
      }
    }
  }

  public isUserOnline(userId?: string | null): boolean {
    if (!userId) return false;
    const list = this.onlineUsersSubject.value;
    return list.includes(userId);
  }

  public clearNotifications(): void {
    this.notifications = [];
    this.notificationsSubject.next([]);
  }

  public getConnectionState(): HubConnectionState {
    return this.hubConnection?.state ?? HubConnectionState.Disconnected;
  }

  public isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  public async disconnect(): Promise<void> {
    this.retryPolicy.reset();
    if (this.hubConnection) {
      this.cleanupSignalREventHandlers();
      await this.hubConnection.stop();
      this.connectionStateSubject.next(HubConnectionState.Disconnected);
    }
  }

  public async reconnect(): Promise<void> {
    this.retryPolicy.reset();
    if (this.hubConnection) {
      await this.hubConnection.stop();
    }
    await this.startConnection();
  }
}
